<template>
    <div class="selector-container">
        
        <div class="text-xl font-bold text-primary mb-6">
            {{ label }}
        </div>
        
        <!-- Search bar -->
        <vs-row v-if="hasItems" class="mb-6">
            <vs-col vs-w="6">
                <vs-input
                    :label="searchText"
                    v-model="search"
                    name="filter"
                    class="w-full"
                />
            </vs-col>
        </vs-row>

        <div class="text-center">
            {{ dragText }}
        </div>

        <vs-row class="mt-2" v-if="hasItems">
            <!-- Left Side (not selected) -->
            <vs-col vs-w="5.5">
                <vs-list>
                    <vs-list-header
                        class="text-lg"
                        :title="availableText"
                        color="primary"
                    />
                    <vs-list class="scrollable_list">
                        
                        <!-- Liste groupée -->
                        <template v-if="groupBy">
                            <template v-for="(value, groupName) in availableList">
                                <vs-list-header
                                    color="primary"
                                    :title="groupName"
                                ></vs-list-header>
                                <draggable
                                    :value="availableList[groupName]"
                                    class="p-2 cursor-move"
                                    group="draggable"
                                >
                                    <vs-list-item
                                        v-for="(availableItem, i) in value"
                                        :key="`available-item-${groupName}-${i}`"
                                        :title="template(availableItem)"
                                    />
                                </draggable>
                            </template>
                        </template>
                        
                        <!-- Liste non groupée -->
                        <template v-else>
                            <draggable
                                class="p-2 cursor-move"
                                group="draggable"
                            >
                                <vs-list-item
                                    v-for="(availableItem, i) in availableList"
                                    :key="`available-item-${i}`"
                                    :title="template(availableItem)"
                                />
                            </draggable>
                        </template>
                    </vs-list>
                </vs-list>
            </vs-col>

            <!-- Icons >> -->
            <template>
                <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-w="1">
                    <chevrons-right-icon
                        size="3x"
                        stroke="rgb(var(--vs-primary))"
                        class="custom-class"
                    />
                </vs-col>
            </template>

            <!-- Right Side (selected) -->
            <vs-col vs-w="5.5">
                <vs-list>
                    <vs-list-header
                        class="text-lg"
                        :title="selectedText"
                        color="primary"
                    />

                    <draggable
                        v-model="selected"
                        group="draggable"
                        class="p-2 cursor-move scrollable_list"
                        :emptyInsertThreshold="250"
                        direction="horizontal"
                        @input="onSelectedChange"
                    >
                        <vs-list-item
                            v-for="(selectedItem, i) in selected"
                            :key="`selected-item-${i}`"
                            :title="template(selectedItem)"
                        />
                    </draggable>
                </vs-list>
            </vs-col>
        </vs-row>
        <vs-row v-else-if="loading" class="selector-container empty">
            <p>Chargement en cours...</p>
        </vs-row>
        <vs-row v-else class="selector-container empty">
            <p>{{ emptyText }}</p>
            <a v-if="addUrl" @click="redirect">{{ addText }}</a>
        </vs-row>
    </div>
</template>

<script>
/**
 * Selection d'élements via groupe, recherche et drag & drop
 * Ce composant est générique à tous types de selection, du moment que les élements disponibles sont regroupés en groupe.
 */

import draggable from "vuedraggable";
import { ChevronsRightIcon } from "vue-feather-icons";

import Fuse from "fuse.js";

export default {
    name: "ItemsSelector",
    components: {
        draggable,
        ChevronsRightIcon
    },
    props: {
        label: {
            type: String,
            required: true
        },
        value: {
            type: Array,
            required: false
        },
        availables: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean,
            required: false,
            default: false
        },
        groupBy: {
            type: Function,
            required: false,
            default: null
        },
        searchText: {
            type: String,
            default: "Filtrer les élements disponibles"
        },
        dragText: {
            type: String,
            default: "Glissez-déposez les élements que vous souhaitez ajouter :"
        },
        availableText: {
            type: String,
            default: "Éléments disponibles"
        },
        selectedText: {
            type: String,
            default: "Éléments sélectionnés"
        },
        emptyText: {
            type: String,
            default: "Aucun élément n'a encore été créé"
        },
        addText: {
            type: String,
            default: "Ajouter un nouvel élément"
        },
        addUrl: {
            type: String,
            required: false
        },
        template: {
            type: Function,
            required: true
        },
        searchBy: {
            type: Array,
            required: false,
            default: ['title', 'description']
        }
    },
    data() {

        return {
            selected: [],
            search: null,
            fuse: null
        };
    },
    created() {

        if (this.value) {
            this.selected = this.value.slice().sort((a, b) => {
                return a.pivot.order - b.pivot.order
            });

            this.selected.map((el, i) => {
                el.order = i;
                return el;
            })
        }

        const fuseOptions = {
            keys: this.searchBy,
            isCaseSensitive: false,
            threshold: 0.4,
            location: 0,
            distance: 100
        };

        this.fuse = new Fuse([], fuseOptions)
    },
    watch: {
        value(value) {
            this.selected = value.slice()
        }
    },
    computed: {
        hasItems() {
            return this.availables && this.availables.length > 0
        },

        establishmentsServices() {
            if (this.isASuperAdmin(this.user) || (this.isAnAdmin(this.user) && this.user.establishments?.length == 0)) {
                return this.establishments.data.flatMap(e => e.services);
            } else if (this.user && this.user.establishments?.length) {
                return this.user.establishments.flatMap(e => e.services);
            }
            return [];
        },
        establishmentsServicesIds() {
            if (!this.establishments.loading) {
                return this.establishmentsServices.flatMap(s => s.id);
            }
            return [];
        },

        availableList() {
            if (!this.availables) {
                return []
            }

            let items = [];

            // Filtre en fonction de la recherche
            if (this.search) {
                this.fuse.setCollection(this.availables);
                items = [...this.fuse.search(this.search)];
            } else {
                items = [...this.availables];
            }

            // Filtre en fonction des élements sélectionnées
            items = items.filter(item => !this.selected.map(s => s.id).includes(item.id))

            // Groupe les éléments via le callback
            let groups = {}
            if (this.groupBy) {
                items.forEach(item => {
                    if (!groups[this.groupBy(item)]) {
                        groups[this.groupBy(item)] = [];
                    }
    
                    groups[this.groupBy(item)].push(item);
                })
                return groups;
            } else {
                return items;
            }
        }
    },
    methods: {
        onSelectedChange() {
            let selected = this.selected.map(function(item, key) {
                item.order = key;
                return item;
            })

            this.$emit("input", selected);
        },
        redirect() {
            window.location.href = this.addUrl
        }
    }
};
</script>

<style lang="scss" scoped>
.selector-container {
    min-height: 200px;
    &.empty {
        background-color: #f8f8f8;
        border-radius: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        a {
            cursor: pointer;
        }
        > * {
            font-size: 1.1em;
        }
    }
    p.title {
        font-size: 1.05em;
    }
    .vs-col {
        padding: 0;
    }
    .scrollable_list {
        height: 250px;
        min-height: 250px;
        overflow-y: auto;
    }
}
</style>
